import React, { Suspense, lazy, useState, useEffect } from 'react'
import { Provider, useSelector, useDispatch } from 'react-redux'
import { Routes, Route, useNavigate, useLocation, useParams } from 'react-router'
import { TransitionGroup, CSSTransition } from 'react-transitioning'
import { NotificationManager, NotificationContainer } from 'react-notifications'
import io from 'socket.io-client'

import 'bootstrap/dist/css/bootstrap.min.css'
import 'react-sliding-pane/dist/react-sliding-pane.css'
import 'react-notifications/lib/notifications.css'
import 'react-inputs-validation/lib/react-inputs-validation.min.css'
import './imports/css/atlantis.min.css'
import './imports/css/demo.css'
import './imports/css/style.css'
import './imports/css/fonts.min.css'
import './imports/css/animation-style.css'
import './imports/css/App.css'
import './imports/css/transitions.css'

import store from './store'
import Authorize from './Authorize'
import usePrev from './hooks/usePrev'
import { Modal } from 'react-bootstrap'
const ErrorHandler = lazy(() => import('./ErrorHandler'))
const NotFound = lazy(() => import('./components/common/NotFound'))
const Landing = lazy(() => import('./components/Landing'))
// const Email = lazy(() => import('./components/Email'))

/* Auth Pages */
const AuthLayout = lazy(() => import('./components/Auth'))
const LoginRedirect = lazy(() => import('./components/Auth/LoginRedirect'))
const VerifyEmail = lazy(() => import('./components/Auth/VerifyEmail'))
const Signup = lazy(() => import('./components/Auth/Signup'))
const Verify = lazy(() => import('./components/Auth/Verify'))
const Login = lazy(() => import('./components/Auth/Login'))
const ForgotPassword = lazy(() => import('./components/Auth/ForgotPassword'))
const ResetPassword = lazy(() => import('./components/Auth/ResetPassword'))
const VerifyHometeam = lazy(() => import('./components/VerifyHometeam'))

const TipsList = lazy(() => import('./components/TipsList'))
const Notifications = lazy(() => import('./components/Notifications'))
// const Search = lazy(() => import('./components/Search'))
const SearchAddressModal = lazy(() => import('./components/SearchAddressModal'))
const SearchAddress = lazy(() => import('./components/SearchAddress'))
const SelectProfile = lazy(() => import('./components/SelectProfile'))
const Unsubscribe = lazy(() => import('./components/Unsubscribe'))

const GeneralSponsorship = lazy(() => import('./components/GeneralSponsorship/index'))
const TipDetail = lazy(() => import('./components/TipDetail'))
const EnterPhone = lazy(() => import('./components/EnterPhone'))
const FAQ = lazy(() => import('./components/FAQ'))

/* Chat module */
const ChatPage = lazy(() => import('./components/Chat/ChatPage'))

/* Account module */
const Account = lazy(() => import('./components/Account/index'))
/* Homeowner */
const HomeownerDashboard = lazy(() => import('./components/Homeowner/index'))
const HomeownerHome = lazy(() => import('./components/Homeowner/Home'))
const HomeownerDocuments = lazy(() => import('./components/Homeowner/Documents'))
const HomeownerMaintenance = lazy(() => import('./components/Homeowner/Maintenance'))
const HomeownerInventory = lazy(() => import('./components/Homeowner/Inventory'))
const HomeownerTeam = lazy(() => import('./components/Homeowner/Team'))
const HomeownerWarranty = lazy(() => import('./components/Homeowner/Warranty'))
const HomeownerHomeInformation = lazy(() => import('./components/Homeowner/HomeInformation'))
const HomeownerReplacementBudget = lazy(() => import('./components/Homeowner/ReplacementBudget'))
const HomeownerPricing = lazy(() => import('./components/Homeowner/Pricing'))
const HomeownerPayment = lazy(() => import('./components/Homeowner/Payment'))
const HomeownerEmergencyEscapePlan = lazy(() => import('./components/Homeowner/EmergencyEscapePlan'))

const HomeownerOnboarding = lazy(() => import('./components/Homeowner/Onboarding'))
const RegisterHomeInformationForm = lazy(() => import('./components/Homeowner/RegisterHomeInformationForm'))
const RegisterInventory = lazy(() => import('./components/Homeowner/RegisterInventory'))
const RegisterHomeTeam = lazy(() => import('./components/Homeowner/RegisterHomeTeam'))
const RegisterCalendars = lazy(() => import('./components/Homeowner/RegisterCalendars'))
const RegisterHomeInformation = lazy(() => import('./components/Homeowner/RegisterHomeInformation'))
const RegistorReplacementBudget = lazy(() => import('./components/Homeowner/RegistorReplacementBudget'))
const HomeownerReport = lazy(() => import('./components/Homeowner/Report'))
const HomeownerInviteRegistration = lazy(() => import('./components/Homeowner/InviteRegistration'))

/* Professional (Realtor, Bulder, Professional) */
const ProfessionalDashboard = lazy(() => import('./components/Professional/index'))
const ProfessionalHome = lazy(() => import('./components/Professional/Home'))
const ProfessionalManageClients = lazy(() => import('./components/Professional/ManageClients'))
const ProfessionalPendingSponsors = lazy(() => import('./components/Professional/PendingSponsors'))
const ProfessionalPendingInvites = lazy(() => import('./components/Professional/PendingInvites'))
const ProfessionalReviews = lazy(() => import('./components/Reviews'))

// const RealtorAddClient = lazy(() => import('./components/RealtorAddClient'))
// const RealtorAddProfessional = lazy(() => import('./components/RealtorAddProfessional'))
// const RealtorOrderClientSubscription = lazy(() =>
//   import('./components/RealtorOrderClientSubscription'),
// )
// const RealtorOrderClientGift = lazy(() => import('./components/RealtorOrderClientGift'))
// const RealtorOrderThanksLetter = lazy(() => import('./components/RealtorOrderThanksLetter'))
// const RealtorThanksLetterCompose = lazy(() => import('./components/RealtorThanksLetterCompose'))
// const RealtorOrderSummary = lazy(() => import('./components/RealtorOrderSummary'))
// const RealtorOrderComplete = lazy(() => import('./components/RealtorOrderComplete'))
const ProfessionalRegistration = lazy(() => import('./components/Professional/Register'))
const InviteClients = lazy(() => import('./components/Professional/InviteClients'))
const SponsorClients = lazy(() => import('./components/Professional/SponsorClients'))
// const AgentProfile = lazy(() => import('./components/AgentProfile'))

/* Builder */
// const BuilderDashboard = lazy(() => import('./components/index'))
// const BuilderAddClient = lazy(() => import('./components/AddClient'))
// const BuilderHomeInventory = lazy(() => import('./components/HomeInventory'))
// const BuilderSubContractor = lazy(() => import('./components/SubContractor'))

/* Professional Search */
const ProfessionalsSearch = lazy(() => import('./components/ProfessionalsSearch/index'))
const ProfessionalsProfile = lazy(() => import('./components/ProfessionalsSearch/Profile'))

/* PropertyManager */
// const PropertyManagerDashboard = lazy(() => import('./components/PropertyManager/index'))
// const PropertyManagerProfile = lazy(() => import('./components/PropertyManager/Profile'))
// const PropertyManagerPropertyList = lazy(() => import('./components/PropertyManager/PropertyList'))

const ReviewForm = lazy(() => import('./components/ReviewForm'))

let versionMsg = false
const App = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const params = useParams()

  const [visible, setVisible] = useState(true)
  const prevLocation = usePrev(location)
  const routeNode = usePrev(null)

  const loaderTimeOut = () => {
    return new Promise((resolve) => {
      setTimeout(() => resolve(), 800)
    })
  }

  useEffect(() => {
    // console.log('init')

    // Connecting to socket
    let socket = io.connect(process.env.REACT_APP_SERVER_URL, { transports: ['websocket'] })
    socket.on('version-check', (currentVersion) => {
      const localVersion = window.localStorage.getItem('client-version')
      // console.log(currentVersion, localVersion)
      if (!localVersion) {
        window.localStorage.setItem('client-version', currentVersion)
      } else if (parseFloat(localVersion) !== currentVersion && !versionMsg) {
        versionMsg = true
        NotificationManager.info(
          'Click to refresh and see updates.',
          'HomeManager Updated!',
          1000 * 60 * 60,
          () => {
            window.localStorage.setItem('client-version', currentVersion)
            window.location.reload(true)
          },
          true,
        )
        setTimeout(
          () => {
            window.localStorage.setItem('client-version', currentVersion)
            window.location.reload(true)
          },
          1000 * 60 * 60,
        )
        window.onbeforeunload = function () {
          window.localStorage.setItem('client-version', currentVersion)
        }
      }
    })

    loaderTimeOut().then(() => {
      setVisible(false)
    })
  }, [])

  const loading = (
    <div
      style={{
        position: 'absolute',
        top: 50,
        zIndex: 1001,
        height: 120,
        width: '100%',
        minWidth: '100px',
        background: 'center/80px no-repeat url(/img/logo-loader-anime.gif) transparent',
      }}
    />
  )

  return (
    <Provider store={store}>
      <Suspense fallback={loading}>
        <ErrorHandler>
          {visible ? loading : null}

          <Authorize>
            <TransitionGroup>
              <CSSTransition
                // nodeRef={routeNode}
                key={location.pathname}
                // mountOnEnter={false}
                // unmountOnExit={false}
                // in={false}
                // appear={false}
                // enter={false}
                // exit={false}
                // timeout={{ appear: 0, enter: 0, exit: 0 }}
                timeout={60000}
                duration={100}
                classNames="fade"
              >
                <Routes ref={routeNode}>
                  {/* Auth pages */}
                  <Route
                    path="/"
                    exact
                    element={<Landing navigate={navigate} location={location} onShowLoading={setVisible} />}
                  />
                  <Route path="/" element={<AuthLayout prevLocation={prevLocation} />}>
                    <Route path="/register/login" exact element={<Login />} />
                    <Route path="/login" exact element={<Login />} />
                    <Route path="/register/logout" exact element={<Login />} />
                    <Route path="/logout" exact element={<Login />} />
                    <Route path="/forgot-password" exact element={<ForgotPassword />} />
                    <Route path="/reset-password/:key" exact element={<ResetPassword />} />
                    <Route
                      path="/register/:userType/:sponsor_invitation_key?"
                      exact
                      element={<Signup navigate={navigate} location={location} />}
                    />
                    <Route path="/register/success" exact element={<Verify />} />
                  </Route>
                  <Route path="/verify-email/:key" exact element={<VerifyEmail />} />

                  {/* Homeowner dashboard */}
                  <Route path="/homeowner" element={<HomeownerDashboard prevLocation={prevLocation} />}>
                    <Route index element={<HomeownerHome />} />
                    <Route path="/homeowner/home" element={<HomeownerHome />} />

                    <Route path="/homeowner/maintenance" element={<HomeownerMaintenance />} />
                    <Route path="/homeowner/team" element={<HomeownerTeam />} />
                    <Route path="/homeowner/homeinformation" element={<HomeownerHomeInformation />} />
                    <Route path="/homeowner/documents" element={<HomeownerDocuments />} />
                    <Route path="/homeowner/homeinventory" element={<HomeownerInventory />} />
                    <Route path="/homeowner/replacementbudget" element={<HomeownerReplacementBudget />} />
                    <Route path="/homeowner/homewarranty" element={<HomeownerWarranty />} />
                    <Route path="/homeowner/emergency-floor-plan" element={<HomeownerEmergencyEscapePlan />} />

                    <Route path="/homeowner/account" element={<Account />} />
                    <Route exact path="/homeowner/chat/:receiver?" element={<ChatPage prevLocation={prevLocation} />} />

                    <Route path="/homeowner/faq" element={<FAQ />} />
                  </Route>

                  {/* Homeowner Onboarding steps */}
                  <Route path="/search-home/:key?" element={<SearchAddress />} />
                  <Route path="/homeowner-invitation/:key?" element={<HomeownerInviteRegistration />} />
                  <Route path="/homeowner-onboarding" element={<HomeownerOnboarding />} />
                  <Route
                    path="/financial-history"
                    element={
                      <RegisterHomeInformationForm
                        location={location}
                        prevLocation={prevLocation}
                        navigate={navigate}
                      />
                    }
                  />
                  <Route
                    path="/register-inventory-interior"
                    element={<RegisterInventory navigate={navigate} location={location} />}
                  />
                  <Route
                    path="/register-inventory-exterior"
                    element={<RegisterInventory navigate={navigate} location={location} />}
                  />
                  <Route path="/register-hometeam" element={<RegisterHomeTeam />} />
                  <Route path="/replacement-budget" element={<RegistorReplacementBudget />} />
                  <Route path="/register-calendars" element={<RegisterCalendars navigate={navigate} />} />
                  <Route
                    path="/register-homeinformation/:property_id/:sponsor_invitation_key?"
                    element={
                      <RegisterHomeInformation
                        location={location}
                        prevLocation={prevLocation}
                        navigate={navigate}
                        params={params}
                      />
                    }
                  />
                  <Route exact path="/homeowner-enterphone" element={<EnterPhone />} />
                  <Route path="/hometeam-status/:key/:action" element={<VerifyHometeam />} />
                  <Route path="/homeowner-report/:key?" element={<HomeownerReport />} />

                  {/* Professional Dashboard */}
                  <Route path="/professional" element={<ProfessionalDashboard prevLocation={prevLocation} />} />
                  <Route path="/realtor" element={<ProfessionalDashboard prevLocation={prevLocation} />} />
                  <Route path="/builder" element={<ProfessionalDashboard prevLocation={prevLocation} />} />
                  <Route path="/professional" element={<ProfessionalDashboard prevLocation={prevLocation} />}>
                    <Route index element={<ProfessionalHome />} />
                    <Route path="/professional/home/:id?" element={<ProfessionalHome />} />
                    <Route path="/professional/chat" exact element={<ChatPage prevLocation={prevLocation} />} />
                    <Route
                      path="/professional/chat/:receiver"
                      exact
                      element={<ChatPage prevLocation={prevLocation} />}
                    />
                    <Route path="/professional/manage-clients" element={<ProfessionalManageClients />} />
                    <Route
                      path="/professional/manage-professionals"
                      element={
                        <HomeownerTeam page="professional" />
                        // <ManageProfessionals />
                      }
                    />
                    <Route path="/professional/pending-sponsors" element={<ProfessionalPendingSponsors />} />
                    {/* <Route path="/professional/pending-invites" element={<ProfessionalPendingInvites />} /> */}
                    <Route path="/professional/reviews" element={<ProfessionalReviews />} />
                    <Route path="/professional/account" element={<Account />} />
                  </Route>
                  {/* Onboarding steps */}
                  {/* <Route path="/realtor-agent-profile" element={<AgentProfile location={location} />}} /> */}
                  {/* <Route path="/builder-agent-profile" element={<AgentProfile location={location} />}} /> */}
                  <Route path="/register-professional" element={<ProfessionalRegistration />} />
                  {/* Add new Client steps */}
                  {/* <Route path="/add-client" element={<RealtorAddClient} />
                  <Route path="/add-professional" element={<RealtorAddProfessional} />
                  <Route
                    path="/add-client-subscription"
                    element={<RealtorOrderClientSubscription}
                  />
                  <Route path="/add-client-gift" element={<RealtorOrderClientGift} />
                  <Route path="/add-letter-tempalate" element={<RealtorOrderThanksLetter} />
                  <Route path="/add-letter-compose" element={<RealtorThanksLetterCompose} />
                  <Route path="/add-order-summary" element={<RealtorOrderSummary} />
                  <Route path="/add-order-placed" element={<RealtorOrderComplete />} /> */}
                  <Route path="/invite-clients" element={<InviteClients />} />
                  <Route path="/sponsor-clients" element={<SponsorClients />} />
                  {/* <Route path="/builder" element={<BuilderDashboard location={location} prevLocation={prevLocation} navigate={navigate} />} /> */}
                  {/* Add new Client steps */}
                  {/* <Route path="/builder-add-client" element={<BuilderAddClient location={location} />} />
                  <Route path="/builder-home-inventory" element={<BuilderHomeInventory location={location} />} />
                  <Route
                    path="/realtor-sub-contractor)"
                    element={<BuilderSubContractor location={location} navigate={navigate} />}
                    exact
                  />
                  <Route
                    path="/builder-sub-contractor)"
                    element={<BuilderSubContractor location={location} navigate={navigate} />}
                    exact
                  /> */}

                  {/* Property Manager */}
                  {/* <Route path="/property-manager" element={<PropertyManagerDashboard prevLocation={prevLocation} /> } />
                  <Route path="/property-manager-profile" element={<PropertyManagerProfile />} />
                  <Route
                    path="/property-manager-add-property"
                    element={<PropertyManagerPropertyList />}
                  /> */}

                  {/* Professional Search, Profile */}
                  <Route path="/search-home-professionals" element={<ProfessionalsSearch />} />
                  <Route path="/homepros-profile/:id" element={<ProfessionalsProfile />} />

                  <Route path="/login-redirect/:page?" element={<LoginRedirect location={location} />} />
                  <Route path="/general-sponsorship/:key?" element={<GeneralSponsorship />} />
                  <Route path="/select-profile" element={<SelectProfile prevLocation={prevLocation} />} />
                  <Route path="/get-review/:id" element={<ReviewForm />} />
                  <Route path="/all-tips" exact element={<TipsList />} />
                  <Route path="/tip/:id?" element={<TipDetail />} />
                  <Route path="/all-notifications" element={<Notifications />} />
                  <Route path="/email-unsubscribe" element={<Unsubscribe />} />
                  <Route
                    path="/address-search"
                    exact
                    element={<SearchAddressModal show={true} userType="homeowner" handleClose={() => {}} />}
                  />
                  <Route path="/error" element={<ErrorHandler />} />

                  <Route path="*" element={<NotFound />} />
                </Routes>
              </CSSTransition>
            </TransitionGroup>
          </Authorize>

          <NotificationContainer />
        </ErrorHandler>
      </Suspense>
    </Provider>
  )
}

export default App
